import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3200';

function authHeaders(): Record<string, string> {
  try {
    const token = (JSON.parse(localStorage.getItem('urs-auth') ?? '{}')?.state ?? {})?.token ?? '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

interface Location {
  id: string;
  name: string;
}

interface RestaurantTable {
  id: string;
  tableNumber: string;
  capacity: number | null;
  section: string | null;
  shape: string;
  isActive: boolean;
}

interface TableFormData {
  tableNumber: string;
  capacity: string;
  section: string;
  shape: 'square' | 'round' | 'rectangle';
}

const EMPTY_FORM: TableFormData = { tableNumber: '', capacity: '', section: '', shape: 'square' };

async function apiFetch<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts?.headers ?? {}) },
  });
  const json = await res.json() as { data: T; error: { message: string } | null };
  if (!res.ok) throw new Error(json.error?.message ?? 'Request failed');
  return json.data;
}

export function PosSettingsPage() {
  const qc = useQueryClient();
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [modal, setModal] = useState<{ mode: 'add' | 'edit'; table?: RestaurantTable } | null>(null);
  const [form, setForm] = useState<TableFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: locations = [] } = useQuery<Location[]>({
    queryKey: ['locations'],
    queryFn: () => apiFetch<Location[]>('/v1/locations'),
  });

  useEffect(() => {
    if (locations.length > 0 && !selectedLocationId) setSelectedLocationId(locations[0]!.id);
  }, [locations, selectedLocationId]);

  const locationId = selectedLocationId || locations[0]?.id;

  const { data: tables = [], isLoading } = useQuery<RestaurantTable[]>({
    queryKey: ['tables', locationId],
    queryFn: () => apiFetch<RestaurantTable[]>(`/v1/reservations/tables/list?locationId=${locationId}&includeInactive=true`),
    enabled: !!locationId,
  });

  const createMutation = useMutation({
    mutationFn: (data: TableFormData) =>
      apiFetch('/v1/reservations/tables', {
        method: 'POST',
        body: JSON.stringify({
          locationId,
          tableNumber: data.tableNumber,
          capacity: data.capacity ? parseInt(data.capacity, 10) : undefined,
          section: data.section || undefined,
          shape: data.shape,
        }),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tables', locationId] }); closeModal(); },
    onError: (e: Error) => setError(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TableFormData & { isActive: boolean }> }) =>
      apiFetch(`/v1/reservations/tables/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          tableNumber: data.tableNumber,
          capacity: data.capacity !== undefined ? (data.capacity ? parseInt(String(data.capacity), 10) : undefined) : undefined,
          section: data.section !== undefined ? (data.section || undefined) : undefined,
          shape: data.shape,
          isActive: data.isActive,
        }),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tables', locationId] }); closeModal(); },
    onError: (e: Error) => setError(e.message),
  });

  function openAdd() {
    setForm(EMPTY_FORM);
    setError(null);
    setModal({ mode: 'add' });
  }

  function openEdit(table: RestaurantTable) {
    setForm({
      tableNumber: table.tableNumber,
      capacity: table.capacity != null ? String(table.capacity) : '',
      section: table.section ?? '',
      shape: table.shape as 'square' | 'round' | 'rectangle',
    });
    setError(null);
    setModal({ mode: 'edit', table });
  }

  function closeModal() {
    setModal(null);
    setForm(EMPTY_FORM);
    setError(null);
    setSaving(false);
  }

  async function handleSave() {
    if (!form.tableNumber.trim()) { setError('Table number is required'); return; }
    setSaving(true);
    setError(null);
    try {
      if (modal?.mode === 'add') {
        await createMutation.mutateAsync(form);
      } else if (modal?.table) {
        await updateMutation.mutateAsync({ id: modal.table.id, data: form });
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(table: RestaurantTable) {
    await updateMutation.mutateAsync({ id: table.id, data: { isActive: !table.isActive } });
  }

  const sections = Array.from(new Set(tables.map((t) => t.section ?? 'Main').filter(Boolean)));
  const active = tables.filter((t) => t.isActive).length;

  return (
    <div>
      <div className="page-header">
        <div>
          <p style={{ color: 'var(--text-3)', fontSize: 13 }}>Manage restaurant tables and floor plan</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={openAdd}
          disabled={!locationId}
        >
          + Add Table
        </button>
      </div>

      {/* Location picker */}
      {locations.length > 1 && (
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-2)' }}>Location:</label>
          <select
            className="input"
            style={{ width: 200 }}
            value={selectedLocationId}
            onChange={(e) => setSelectedLocationId(e.target.value)}
          >
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total Tables', value: tables.length },
          { label: 'Active', value: active },
          { label: 'Sections', value: sections.length },
        ].map((s) => (
          <div key={s.label} className="stat-card" style={{ textAlign: 'center' }}>
            <p className="stat-value">{s.value}</p>
            <p className="stat-label">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Table grid */}
      {isLoading ? (
        <div style={{ textAlign: 'center', color: 'var(--text-3)', padding: '48px 0', fontSize: 13 }}>Loading tables…</div>
      ) : tables.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '64px 0', color: 'var(--text-3)' }}>
          <p style={{ fontSize: 36, marginBottom: 12 }}>🪑</p>
          <p style={{ fontWeight: 500, color: 'var(--text-2)' }}>No tables yet</p>
          <p style={{ fontSize: 13, marginTop: 4 }}>Click "+ Add Table" to create your first table.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {sections.map((sec) => {
            const sectionTables = tables.filter((t) => (t.section ?? 'Main') === sec);
            return (
              <div key={sec}>
                <h2 style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>{sec}</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
                  {sectionTables.map((table) => (
                    <div
                      key={table.id}
                      className="card"
                      style={{
                        opacity: table.isActive ? 1 : 0.5,
                        border: table.isActive ? '2px solid var(--border)' : '2px dashed var(--border)',
                        padding: 14,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{table.tableNumber}</span>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: table.isActive ? 'var(--green)' : 'var(--text-3)', marginTop: 4 }} />
                      </div>
                      {table.capacity && (
                        <p style={{ color: 'var(--text-3)', fontSize: 12 }}>👥 {table.capacity} seats</p>
                      )}
                      <p style={{ color: 'var(--text-3)', fontSize: 12, textTransform: 'capitalize' }}>{table.shape}</p>
                      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ flex: 1, justifyContent: 'center' }}
                          onClick={() => openEdit(table)}
                        >
                          Edit
                        </button>
                        <button
                          className={`btn btn-sm ${table.isActive ? 'btn-danger' : 'btn-ghost'}`}
                          style={{ flex: 1, justifyContent: 'center', color: table.isActive ? undefined : 'var(--green)' }}
                          onClick={() => handleToggleActive(table)}
                        >
                          {table.isActive ? 'Off' : 'On'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit modal */}
      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{modal.mode === 'add' ? 'Add Table' : `Edit Table ${modal.table?.tableNumber}`}</h2>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {error && (
                <div style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', color: 'var(--red)', fontSize: 13, borderRadius: 8, padding: '8px 12px' }}>
                  {error}
                </div>
              )}
              <div className="field">
                <label className="field-label">Table Number *</label>
                <input
                  className="input"
                  type="text"
                  value={form.tableNumber}
                  onChange={(e) => setForm((f) => ({ ...f, tableNumber: e.target.value }))}
                  placeholder="e.g. T1, A5, VIP-2"
                  maxLength={20}
                />
              </div>
              <div className="field">
                <label className="field-label">Capacity (seats)</label>
                <input
                  className="input"
                  type="number"
                  value={form.capacity}
                  onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))}
                  placeholder="e.g. 4"
                  min={1}
                  max={50}
                />
              </div>
              <div className="field">
                <label className="field-label">Section</label>
                <input
                  className="input"
                  type="text"
                  value={form.section}
                  onChange={(e) => setForm((f) => ({ ...f, section: e.target.value }))}
                  placeholder="e.g. Main Dining, Terrace, VIP"
                  maxLength={100}
                  list="section-suggestions"
                />
                <datalist id="section-suggestions">
                  {sections.map((s) => <option key={s} value={s} />)}
                </datalist>
              </div>
              <div className="field">
                <label className="field-label">Shape</label>
                <select
                  className="input"
                  value={form.shape}
                  onChange={(e) => setForm((f) => ({ ...f, shape: e.target.value as TableFormData['shape'] }))}
                >
                  <option value="square">Square</option>
                  <option value="round">Round</option>
                  <option value="rectangle">Rectangle</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : modal.mode === 'add' ? 'Add Table' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
